import { useState } from 'react';
import { MdClose, MdWarning, MdExpandMore, MdCalendarToday, MdLock } from 'react-icons/md';
import toast from 'react-hot-toast';
import { useCreateRecord } from '../../hooks/useStudentRecords';
import { adToBs } from '../../utils/calendar';
import StudentSelect from '../common/StudentSelect';
import useTheme from '../../context/ThemeContext';
import useClubVocab from '../../hooks/useClubVocab';

// Kathmandu-aware "today" (UTC+5:45) as AD ISO "YYYY-MM-DD" — avoids the UTC
// midnight-boundary bug that toISOString() has in NPT.
const KTM_OFFSET_MS = (5 * 60 + 45) * 60 * 1000;
const ktmTodayISO = () => {
  const k = new Date(Date.now() + KTM_OFFSET_MS);
  return `${k.getUTCFullYear()}-${String(k.getUTCMonth() + 1).padStart(2, '0')}-${String(k.getUTCDate()).padStart(2, '0')}`;
};

// Helpers to handle flexible student object shapes
const getStudentDisplayName = (student) => {
  if (!student) return 'Unnamed student';

  // Try nested user.name first (StudentProfile structure from API)
  if (student?.user?.name) return student.user.name;

  // Try fullName (StudentProfile field)
  if (student?.fullName) return student.fullName;

  // Try direct name field
  if (student?.name) return student.name;

  // Try first/last name combination
  const firstLast = [student?.firstName, student?.lastName].filter(Boolean).join(' ').trim();
  if (firstLast) return firstLast;

  // Try email or ID as last resort
  if (student?.email) return student.email;
  if (student?.studentId) return student.studentId;
  if (student?._id) return student._id;

  return 'Unnamed student';
};

const getStudentCode = (student) => {
  if (!student) return '';
  return (
    student?.studentId ||
    student?.studentCode ||
    student?.code ||
    student?.registrationId ||
    ''
  );
};

const getStudentSubmitId = (student) => {
  // Backend expects User._id, not StudentProfile._id
  return student?.user?._id || student?.user || student?._id;
};

const CATEGORIES = {
  progress_note: [
    { value: 'technique', label: 'Technique' },
    { value: 'attendance', label: 'Attendance' },
    { value: 'belt_readiness', label: 'Belt Readiness' },
    { value: 'behavior', label: 'Behavior' },
    { value: 'general', label: 'General' },
  ],
  incident_report: [
    { value: 'injury', label: 'Injury' },
    { value: 'safety', label: 'Safety' },
    { value: 'behavior', label: 'Behavior' },
    { value: 'other', label: 'Other' },
  ],
};

function CreateRecordModal({ isOpen, onClose, students = [], onSuccess, isLoadingStudents = false }) {
  const v = useClubVocab();
  const [recordType, setRecordType] = useState('progress_note');
  const [studentId, setStudentId] = useState('');
  const [category, setCategory] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [date] = useState(ktmTodayISO()); // always today (Kathmandu); not user-editable
  const [severity, setSeverity] = useState('medium');
  const [actionTaken, setActionTaken] = useState('');
  const [followUpRequired, setFollowUpRequired] = useState(false);
  const [errors, setErrors] = useState({});
  const currentTheme = useTheme()?.currentTheme;

  const { mutate: createRecord, isPending } = useCreateRecord();

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});

    const newErrors = {};
    if (!studentId) newErrors.studentId = `${v.member} is required`;
    if (!title || title.trim().length < 2) newErrors.title = 'Title must be at least 2 characters';
    if (!content || content.trim().length < 5) newErrors.content = 'Content must be at least 5 characters';
    if (!date) newErrors.date = 'Date is required';
    if (!category) newErrors.category = 'Category is required';
    if (recordType === 'incident_report' && !severity) newErrors.severity = 'Severity is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const payload = {
      studentId,
      recordType,
      category,
      title: title.trim(),
      content: content.trim(),
      date,
    };

    if (recordType === 'incident_report') {
      payload.severity = severity;
      payload.actionTaken = actionTaken.trim();
      payload.followUpRequired = followUpRequired;
    }

    createRecord(payload, {
      onSuccess: () => {
        toast.success(`${recordType === 'progress_note' ? 'Progress Note' : 'Incident Report'} created successfully!`);
        onClose();
        if (onSuccess) onSuccess();
        // Reset form
        setStudentId('');
        setTitle('');
        setContent('');
        setCategory('');
        setSeverity('medium');
        setActionTaken('');
        setFollowUpRequired(false);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Failed to create record');
      },
    });
  };

  const handleClose = () => {
    if (isPending) return;
    onClose();
  };

  const categories = CATEGORIES[recordType] || [];
  const studentName = students.find(s => s._id === studentId)?.name || '';

  if (!isOpen) return null;

  const labelCls  = 'block text-sm font-medium text-secondary mb-1.5';
  const inputCls  = (err) => `w-full bg-card border rounded-xl px-3 py-2 text-sm text-secondary placeholder-subtle focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-secondary transition-colors ${err ? 'border-red-500' : 'border-divider'}`;
  const selectCls = (err) => `w-full appearance-none bg-card border rounded-full pl-3 pr-9 py-2 text-sm text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-secondary transition-colors ${err ? 'border-red-500' : 'border-divider'}`;

  // Calm tinted "active" state for segmented toggles (soft tints, readable in both themes).
  const segActive = {
    progress_note:  'bg-green-500/15 text-green-600 border-green-500/30',
    incident_report:'bg-red-500/15 text-red-600 border-red-500/30',
    low:            'bg-green-500/15 text-green-600 border-green-500/30',
    medium:         'bg-amber-500/15 text-amber-600 border-amber-500/30',
    high:           'bg-red-500/15 text-red-600 border-red-500/30',
  };
  const segBase   = 'py-2 rounded-full text-sm font-semibold border transition-colors disabled:opacity-50';
  const segIdle   = 'bg-card text-muted border-divider hover:bg-nav-hover';

  // Read-only "today" stamp (BS primary · AD secondary) — records are always dated today.
  let bsLabel = '', adLabel = '';
  try {
    const d = new Date(`${date}T00:00:00`);
    const bs = adToBs(d);
    bsLabel = `${bs.monthName} ${bs.day}, ${bs.year}`;
    adLabel = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { /* leave blank */ }
  const dateStamp = (
    <div>
      <label className={labelCls}>Date</label>
      <div
        className="flex items-center gap-2 bg-nav-hover border border-divider rounded-xl px-3 py-2 text-sm"
        title="Records are always dated today and cannot be changed."
      >
        <MdCalendarToday className="text-muted text-base flex-shrink-0" />
        <span className="text-secondary font-medium">{bsLabel}</span>
        <span className="text-subtle">·</span>
        <span className="text-muted">{adLabel}</span>
        <MdLock className="text-subtle text-sm ml-auto flex-shrink-0" />
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div className="relative bg-card border border-divider rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-divider flex-shrink-0">
          <h2 className="font-bold text-secondary text-lg">
            {recordType === 'progress_note' ? 'Create progress note' : 'Create incident report'}
          </h2>
          <button
            onClick={handleClose}
            disabled={isPending}
            className="w-8 h-8 flex items-center justify-center rounded-full text-muted hover:bg-nav-hover transition-colors disabled:opacity-50"
          >
            <MdClose className="text-xl" />
          </button>
        </div>

        {/* Form: scrollable body + pinned footer */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3.5">
            {/* Locked notice — slim single line */}
            <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 text-xs text-amber-700">
              <MdWarning className="text-amber-500 text-base flex-shrink-0" />
              <span>Once submitted, a record can&apos;t be edited — add a follow-up note instead.</span>
            </div>

            {/* Record Type — segmented toggle */}
            <div>
              <label className={labelCls}>Record type *</label>
              <div className="grid grid-cols-2 gap-2">
                {[['progress_note', 'Progress note'], ['incident_report', 'Incident']].map(([val, lbl]) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => { setRecordType(val); setCategory(''); }}
                    disabled={isPending}
                    aria-pressed={recordType === val}
                    className={`${segBase} ${recordType === val ? segActive[val] : segIdle}`}
                  >
                    {lbl}
                  </button>
                ))}
              </div>
            </div>

            {/* Student + Category */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>{v.member} *</label>
                <StudentSelect
                  students={students}
                  value={studentId}
                  onChange={setStudentId}
                  loading={isLoadingStudents}
                  disabled={isPending || isLoadingStudents || students.length === 0}
                  placeholder={students.length === 0 ? `No ${v.members.toLowerCase()} found` : `Select a ${v.member.toLowerCase()}`}
                  getOptionValue={getStudentSubmitId}
                  getOptionLabel={getStudentDisplayName}
                  getOptionMeta={getStudentCode}
                  error={!!errors.studentId}
                />
                {errors.studentId && <p className="text-xs text-red-500 mt-1">{errors.studentId}</p>}
                {students.length === 0 && !isLoadingStudents && (
                  <p className="text-xs text-muted mt-1">No {v.members.toLowerCase()} available. Check your club roster.</p>
                )}
              </div>

              <div>
                <label className={labelCls}>Category *</label>
                <div className="relative">
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    disabled={isPending}
                    className={selectCls(errors.category)}
                  >
                    <option value="">Select category</option>
                    {categories.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                  <MdExpandMore className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted text-lg pointer-events-none" />
                </div>
                {errors.category && <p className="text-xs text-red-500 mt-1">{errors.category}</p>}
              </div>
            </div>

            {/* Title */}
            <div>
              <div className="flex items-baseline justify-between">
                <label className={labelCls}>Title *</label>
                <span className="text-xs text-muted">{title.length}/100</span>
              </div>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={isPending}
                placeholder="e.g. Strong sparring performance"
                maxLength={100}
                className={inputCls(errors.title)}
              />
              {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title}</p>}
            </div>

            {/* Content */}
            <div>
              <div className="flex items-baseline justify-between">
                <label className={labelCls}>Content *</label>
                <span className="text-xs text-muted">{content.length}/2000</span>
              </div>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                disabled={isPending}
                placeholder="Describe what happened, key observations, or feedback…"
                maxLength={2000}
                rows={4}
                className={`${inputCls(errors.content)} resize-none`}
              />
              {errors.content && <p className="text-xs text-red-500 mt-1">{errors.content}</p>}
            </div>

            {/* Date (read-only today stamp) + Severity for incident */}
            {recordType === 'incident_report' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {dateStamp}
                <div>
                  <label className={labelCls}>Severity *</label>
                  <div className="grid grid-cols-3 gap-2">
                    {['low', 'medium', 'high'].map(level => (
                      <button
                        key={level}
                        type="button"
                        onClick={() => setSeverity(level)}
                        disabled={isPending}
                        aria-pressed={severity === level}
                        className={`${segBase} capitalize ${severity === level ? segActive[level] : segIdle}`}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                  {errors.severity && <p className="text-xs text-red-500 mt-1">{errors.severity}</p>}
                </div>
              </div>
            ) : (
              dateStamp
            )}

            {/* Incident-only: action taken + follow-up */}
            {recordType === 'incident_report' && (
              <>
                <div>
                  <div className="flex items-baseline justify-between">
                    <label className={labelCls}>Action taken</label>
                    <span className="text-xs text-muted">{actionTaken.length}/500</span>
                  </div>
                  <textarea
                    value={actionTaken}
                    onChange={(e) => setActionTaken(e.target.value)}
                    disabled={isPending}
                    placeholder="e.g. Ice applied, referred to physiotherapist…"
                    maxLength={500}
                    rows={2}
                    className={`${inputCls(false)} resize-none`}
                  />
                </div>

                <label className="flex items-center gap-2 cursor-pointer text-sm text-secondary">
                  <input
                    type="checkbox"
                    checked={followUpRequired}
                    onChange={(e) => setFollowUpRequired(e.target.checked)}
                    disabled={isPending}
                    className="w-4 h-4 accent-primary"
                  />
                  Follow-up required
                </label>
              </>
            )}
          </div>

          {/* Footer (pinned) */}
          <div className="flex items-center gap-3 px-6 py-4 border-t border-divider flex-shrink-0">
            <button
              type="button"
              onClick={handleClose}
              disabled={isPending}
              className="flex-1 px-4 py-2.5 bg-card border border-divider rounded-full text-secondary font-medium hover:bg-nav-hover transition-all duration-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className={`flex-1 px-4 py-2.5 ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} font-medium rounded-full hover:opacity-90 transition-all duration-200 disabled:opacity-50`}
            >
              {isPending ? 'Creating…' : 'Create record'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateRecordModal;
